/**
 * rt-smart-upsell.js - Rawtorque Smart Upsell Engine v1.8
 *
 * v1.8 - Intent + Collection Push:
 *   - Hard car↔bike guard: a bike product can never appear on a car page
 *     (or vice versa) - fixes JASO-MA2 motorcycle oil showing on a car
 *     cold-air-intake.
 *   - Intent-aware: performance buyers (intake/exhaust/ECU) get performance/
 *     tuning complements first, not plain maintenance oil.
 *   - Collection CTA (hybrid): every product maps to its best collection
 *     (e.g. Cold Air Intake → "Car Performance Upgrades"). Shown as an
 *     "Explore more in …" link under the bundle; when there's no confident
 *     bundle the collection becomes the primary card (solo mode).
 *
 * v1.7 - Coverage + Car Fitment:
 *   - COMPLEMENT/type maps expanded to cover the full catalogue (coolant,
 *     additives, cold air intake, wiring harness, pod filter, fog lamp,
 *     navigation, paddock stand, etc.) + fixed type-name typos
 *     ("fog lamp"→fog light, "sum guard"→sump guard).
 *   - Car awareness: car products only auto-upsell fitment-SAFE universal
 *     items (oil/coolant/additive/care) - no model-specific cross-matches
 *     (fixes the Swift-plug→Jimny-filter wrong-fit bug).
 *   - Tier-3 universal fallback: when rules underfill, fall back to Shopify's
 *     store-curated "complementary" recommendations so every product shows
 *     something relevant instead of an empty widget.
 *   - Fixed scoreOne same-family return shape + findUpsells d/data ref.
 *
 * v1.6 - Expert Salesman Mode:
 *   - BENEFIT_COPY: every upsell now shows WHY the rider needs it
 *   - Helmet → Bluetooth intercom upsell added
 *   - L0 metafield layer: admin can pin exact upsell products per product
 */
(function () {
  'use strict';

  const DEBUG = true; // open browser console → F12 → Console tab to see scoring

  // RT PERF: resize Shopify-served images to the displayed box.
  // Upsell thumbs arrive from /products/{handle}.js at native size
  // (e.g. 1000x1000) and were painted into a 48px box. 48 @2x = 96.
  const rtImg = (url, w) => {
    if (!url) return '';
    try {
      const u = new URL(url, location.origin);
      const isShopify = /cdn\.shopify\.com$/i.test(u.hostname)
        || /myshopify\.com$/i.test(u.hostname)
        || /\/(cdn\/shop|files)\//i.test(u.pathname);
      if (!isShopify) return url;
      u.searchParams.delete('height');
      u.searchParams.set('width', String(w));
      return u.toString();
    } catch (e) { return url; }
  };

  // ── COMPLEMENT MAP ─────────────────────────────────────────────────────────
  const COMPLEMENT = {
    // Exhaust / silencer → service items that make sense post-install
    'exhaust':         ['spark plug', 'air filter', 'engine oil', 'oil filter'],
    'silencer':        ['spark plug', 'air filter', 'engine oil', 'oil filter'],
    'muffler':         ['spark plug', 'air filter', 'engine oil', 'oil filter'],
    // Hazard module / flash → lighting upgrades
    'hazard module':   ['fog light', 'led headlight', 'led tail light', 'indicator'],
    'flash module':    ['fog light', 'led headlight', 'led tail light', 'indicator'],
    'flasher relay':   ['fog light', 'indicator', 'led tail light'],
    // Helmet → Bluetooth intercom first, then visor
    'helmet':          ['bluetooth intercom', 'visor', 'helmet bag', 'grip set'],
    // Bluetooth intercom → suggest helmet if they don't have one
    'bluetooth intercom': ['helmet', 'grip set', 'bar end'],
    'intercom':        ['helmet', 'grip set'],
    // Service
    'spark plug':      ['air filter', 'engine oil', 'oil filter'],
    'iridium plug':    ['air filter', 'engine oil', 'oil filter'],
    'spark plugs':     ['air filter', 'engine oil', 'oil filter'],
    'air filter':      ['spark plug', 'exhaust', 'engine oil', 'oil filter'],
    'air cleaner':     ['spark plug', 'exhaust', 'engine oil', 'oil filter'],
    'oil filter':      ['engine oil', 'air filter', 'spark plug'],
    'engine oil':      ['oil filter', 'air filter', 'spark plug'],
    'gear oil':        ['engine oil', 'oil filter'],
    'brake fluid':     ['brake pad', 'brake disc'],
    'fork oil':        ['oil filter', 'fork seal'],
    // Chain / drivetrain
    'chain lube':      ['chain cleaner', 'chain', 'sprocket kit'],
    'chain lubricant': ['chain cleaner', 'chain', 'sprocket kit'],
    'chain cleaner':   ['chain lube', 'chain', 'sprocket kit'],
    'chain oil':       ['chain cleaner', 'chain lube', 'chain'],
    // Chain / sprocket kit buyer already has the drivetrain - sell maintenance
    'chain':           ['chain lubricant', 'chain cleaner'],
    'sprocket':        ['chain lubricant', 'chain cleaner'],
    'sprocket kit':    ['chain lubricant', 'chain cleaner'],
    'clutch plate':    ['clutch spring', 'clutch cable'],
    'clutch cable':    ['brake cable', 'clutch plate'],
    'brake cable':     ['clutch cable'],
    // Brakes
    'brake pad':       ['brake disc', 'brake fluid', 'brake caliper'],
    'brake pads':      ['brake disc', 'brake fluid'],
    'brake disc':      ['brake pad', 'brake fluid', 'brake caliper'],
    'brake shoe':      ['brake drum', 'brake fluid'],
    'brake drum':      ['brake shoe', 'brake fluid'],
    'brake caliper':   ['brake pad', 'brake disc', 'brake fluid'],
    // Tyres
    'tyre':            ['tube', 'tyre sealant', 'rim tape'],
    'tube':            ['tyre', 'rim tape'],
    // Lighting - fog / auxiliary light: switch harness first, then dimmer as separate slot
    'fog light':       ['switch kit', 'dimmer', 'wiring harness', 'led headlight', 'indicator', 'handlebar', 'grip set'],
    'auxiliary light': ['switch kit', 'dimmer', 'wiring harness', 'led headlight', 'indicator', 'handlebar', 'grip set'],
    'led pod':         ['switch kit', 'dimmer', 'wiring harness', 'fog light', 'indicator', 'handlebar', 'grip set'],
    // Switch kit / dimmer - reciprocal
    'switch kit':      ['fog light', 'auxiliary light', 'led headlight', 'indicator', 'dimmer'],
    'dimmer':          ['fog light', 'auxiliary light', 'led pod', 'switch kit'],
    'indicator':       ['headlight', 'tail light', 'fog light'],
    'headlight':       ['indicator', 'tail light', 'fog light'],
    'led headlight':   ['indicator', 'tail light', 'fog light'],
    'tail light':      ['indicator', 'headlight'],
    'led tail light':  ['indicator', 'headlight'],
    'led kit':         ['headlight', 'indicator', 'tail light'],
    'turn signal':     ['headlight', 'tail light', 'hazard module'],
    'battery':         ['battery charger', 'spark plug'],
    // Guards
    'crash guard':     ['engine guard', 'saree guard', 'grip set', 'bar end', 'tank pad'],
    'engine guard':    ['crash guard', 'sump guard', 'grip set', 'bar end'],
    'frame slider':    ['crash guard', 'engine guard', 'bar end'],
    'sump guard':      ['engine guard', 'crash guard'],
    'saree guard':     ['crash guard', 'engine guard'],
    'leg guard':       ['saree guard', 'crash guard'],
    'tank guard':      ['tank pad', 'crash guard', 'grip set'],
    'tank pad':        ['tank guard', 'seat cover', 'grip set'],
    'seat cover':      ['grip set', 'tank pad'],
    // Ergonomics
    'handlebar':       ['grip set', 'bar end', 'mirror'],
    'grip':            ['handlebar', 'bar end', 'lever set'],
    'grip set':        ['handlebar', 'bar end', 'lever set'],
    'bar end':         ['grip set', 'handlebar', 'mirror'],
    'mirror':          ['grip set', 'handlebar', 'indicator'],
    'lever set':       ['grip set', 'brake fluid', 'clutch cable'],
    // Suspension - upgrade-mode buyer: show performance upgrades, then protection
    'suspension':      ['exhaust', 'air filter', 'chain', 'crash guard', 'engine guard', 'fork oil', 'engine oil'],
    'fork':            ['fork oil', 'engine oil', 'brake fluid'],
    // Phone holder / mounts
    'phone holder':    ['grip set', 'bar end', 'handlebar', 'usb charger'],
    'phone mount':     ['grip set', 'handlebar', 'usb charger'],
    // Windscreen / windshield
    'windscreen':      ['crash guard', 'engine guard', 'grip set'],
    'windshield':      ['crash guard', 'engine guard', 'grip set'],
    // Radiator guard
    'radiator guard':  ['engine guard', 'crash guard', 'coolant'],
    // Fuel tuner / ECU / autotune - performance upgrade buyer
    'fuel tuner':      ['exhaust', 'air filter', 'spark plug'],
    'autotune':        ['exhaust', 'air filter', 'spark plug'],
    'ecu':             ['exhaust', 'air filter', 'spark plug'],
    'fuel module':     ['exhaust', 'air filter', 'spark plug'],
    'swing arm stand': ['chain lubricant', 'chain cleaner', 'chain'],
    'rearset':         ['footpegs', 'lever set', 'grip set'],
    // Electrical / lighting coverage gaps
    'wiring harness':  ['switch kit', 'fog light', 'led headlight', 'dimmer'],
    'fog lamp':        ['switch kit', 'dimmer', 'wiring harness', 'led headlight'],
    'led light':       ['switch kit', 'dimmer', 'wiring harness'],
    // Filter naming gaps
    'pod filter':      ['spark plug', 'engine oil', 'exhaust', 'air filter'],
    'cold air intake': ['air filter', 'spark plug', 'engine oil', 'ecu'],
    // Service / fluids - also the safe universal set for cars
    'coolant':         ['engine oil', 'brake fluid', 'additive', 'car care'],
    'additive':        ['engine oil', 'air filter', 'spark plug', 'coolant'],
    'fuel optimizer':  ['engine oil', 'air filter', 'spark plug'],
    'engine treatment':['engine oil', 'oil filter', 'additive'],
    'transmission fluid': ['engine oil', 'coolant'],
    'hydraulic fluid': ['brake fluid', 'engine oil'],
    // Care products
    'car care':        ['cleaner', 'engine oil', 'coolant'],
    'cleaner':         ['car care', 'engine oil', 'chain lube'],
    'helmet care':     ['visor', 'helmet bag', 'bluetooth intercom'],
    // Guard typo + misc fitment-safe
    'sum guard':       ['engine guard', 'crash guard', 'grip set'],
    'navigation':      ['bluetooth intercom', 'phone mount', 'grip set'],
    'paddock stand':   ['chain lubricant', 'chain cleaner', 'chain', 'sprocket kit'],
    'handlebar riser': ['grip set', 'bar end', 'mirror'],
  };

  // Title keyword → inferred product type (when product_type field is blank)
  const TITLE_TYPES = [
    ['exhaust',        ['exhaust']],
    ['silencer',       ['silencer', 'muffler', 'slip-on', 'slip on']],
    ['hazard module',  ['hazard flash', 'flashx', 'flash module', 'hazard module', 'flasher relay', 'blinker relay']],
    ['switch kit',     ['switch kit', 'wiring harness', 'relay harness', 'switch harness', 'led switch', 'rocker switch', 'toggle switch', 'switch relay', 'harness kit']],
    ['dimmer',         ['dimmer', 'brightness controller', 'brightness control', 'quad-level', 'quad level']],
    ['helmet',         ['full face helmet', 'half face helmet', 'open face helmet', 'modular helmet', 'flip up helmet', 'motocross helmet', 'off road helmet', 'smk', 'studds helmet', 'steelbird helmet', 'vega helmet', 'ls2 helmet', 'arai helmet', 'shoei helmet']],
    ['bluetooth intercom', ['bluetooth intercom', 'intercom', 'cardo', 'sena ', 'fodsports', 'b4fm', 'b4plus', 'helmet communication', 'helmet headset', 'motorcycle intercom', 'rider communication', 'helmet speaker', 'helmet bluetooth']],
    ['visor',          ['visor', 'face shield', 'helmet shield', 'helmet visor']],
    ['suspension',     ['suspension', 'air born', 'air-born', 'shock absorber', 'monoshock', 'rear shock', 'front fork suspension', 'performance suspension', 'adjustable suspension']],
    ['fork',           ['fork oil', 'fork seal', 'fork leg', 'fork tube']],
    ['phone holder',   ['phone holder', 'phone mount', 'mobile holder', 'mobile mount', 'smartphone holder', 'phone clamp']],
    ['windscreen',     ['windscreen', 'windshield', 'visor screen', 'wind deflector']],
    ['radiator guard', ['radiator guard', 'radiator cover', 'radiator protector']],
    ['fuel tuner',     ['fuelx', 'fuel x', 'autotune', 'fuel tuner', 'fuel controller', 'fuel module', 'ecu flash', 'piggyback ecu', 'race dynamics', 'bazzaz', 'powertronic', 'traction control module', 'quickshifter']],
    ['led pod',        ['led pod', 'led pods', 'compact led', 'pod lamp', 'spot light', 'spotlight', 'auxiliary led']],
    ['crash guard',    ['crash guard', 'crashguard', 'crash-guard', 'knuckle guard', 'thanos guard']],
    ['engine guard',   ['engine guard', 'sump guard', 'belly pan']],
    ['saree guard',    ['saree guard', 'leg guard']],
    ['frame slider',   ['frame slider', 'engine slider']],
    ['spark plug',     ['spark plug', 'iridium plug', 'ngk plug', 'bosch plug']],
    ['air filter',     ['air filter', 'air cleaner', 'performance filter']],
    ['oil filter',     ['oil filter']],
    ['engine oil',     ['engine oil', 'motor oil', '10w30', '10w40', '20w40', '15w50']],
    ['brake pad',      ['brake pad', 'disc pad', 'sintered brake', 'brakepads']],
    ['brake disc',     ['brake disc', 'brake rotor', 'disc rotor']],
    ['chain',          ['drive chain', 'o-ring chain', 'chain kit', 'chain set']],
    ['sprocket kit',   ['sprocket kit', 'sprocket set', 'chain sprocket']],
    ['grip set',       ['grip set', 'handlebar grip', 'bar grip']],
    ['bar end',        ['bar end', 'bar-end', 'handlebar end', 'handlebar weight']],
    ['mirror',         ['rear view mirror', 'side mirror', 'wing mirror', 'rvm']],
    ['headlight',      ['headlight', 'head lamp', 'projector headlight', 'led headlight']],
    ['indicator',      ['turn signal', 'blinker', 'indicator light', 'led indicator', 'led turn']],
    ['tail light',     ['tail light', 'tail lamp', 'brake light', 'led tail']],
    ['seat cover',     ['seat cover', 'seat protector']],
    ['tank pad',       ['tank pad', 'tank protector', 'fuel tank cover', 'tank grip']],
    ['tyre',           ['tyre', 'tire']],
    ['tube',           ['inner tube', 'tube tyre']],
    ['battery',        ['battery', 'gel battery', 'lead acid']],
  ];

  // Brand-only tags - sharing just a brand is NOT a fitment signal.
  // "Honda CB200X guard" and "Honda CB350 suspension" both have 'honda'
  // but are for completely different bikes. Model tags must match.
  const BRAND_TAGS = new Set([
    'honda', 'royal-enfield', 'bajaj', 'tvs', 'yamaha', 'suzuki',
    'kawasaki', 'ktm', 'hero', 'jawa', 'triumph', 'benelli',
    'ducati', 'harley', 'bmw', 'husqvarna', 'cfmoto',
  ]);
  const CATEGORY_PAIRS = [
    ['crash', 'guard'], ['engine', 'guard'],
    ['spark', 'plug'], ['air', 'filter'], ['oil', 'filter'],
    ['brake', 'pad'], ['brake', 'disc'], ['brake', 'shoe'], ['brake', 'fluid'],
    ['chain', 'lube'], ['chain', 'kit'], ['grip', 'set'],
    ['seat', 'cover'], ['tank', 'pad'],
    ['flash', 'module'], ['hazard', 'flash'], ['hazard', 'module'],
    ['fog', 'light'], ['led', 'headlight'], ['fork', 'seal'], ['clutch', 'plate'],
    ['bar', 'end'], ['lever', 'set'], ['barrel', 'exhaust'],
    ['twin', 'exhaust'], ['galactic', 'exhaust'],
    ['face', 'helmet'], // belt+suspenders: catches helmet-vs-helmet even if TERMINAL_TYPES misses
  ];

  // Same-terminal-type: never suggest another one of these
  const TERMINAL_TYPES = new Set([
    'exhaust', 'silencer', 'muffler', 'hazard module', 'flash module',
    'battery', 'tyre', 'tube', 'fog light',
    'helmet',      // never show another helmet for a helmet buyer
    'suspension',  // never show another suspension for a suspension buyer
    'bluetooth intercom', // never show another intercom
  ]);

  const VEHICLE_PATTERNS = [
    'honda','cb350','cb300','cb200','cb150','cb500','hness','interceptor',
    'continental-gt','cb-shine','unicorn','hornet',
    'royal-enfield','hunter-350','meteor-350','thunderbird','himalayan',
    'scram-411','classic-350','classic-reborn','bullet-350','super-meteor','shotgun','guerrilla',
    'ktm','duke-200','duke-250','duke-390','390-adventure','duke-125',
    'rc390','rc200','rc125',
    'tvs','apache','rtx','rtx-300','rr310','ntorq','ronin','raider','jupiter',
    'bajaj','pulsar','dominar','ns200','ns160','rs200','avenger',
    'yamaha','r15','mt-15','fz','fzs','fz25','mt07','mt09','r3','r7',
    'suzuki','gixxer','gsx','hayabusa','vstrom','burgman',
    'kawasaki','ninja','versys','z900','z650','z400','ninja400','ninja650','z1000',
    'bmw','g310','g310r','g310rr','g 310','g 310 r','g 310 rr','s1000','s1000rr','f900','f850','r1250','r1200',
    'hero','xpulse','xtreme','splendor','passion',
    'yezdi','yezdi-adventure','yezdi-scrambler','yezdi-roadster',
    'jawa','triumph','benelli','ducati','harley',
    'interceptor-650','continental-650',
  ];

  // ── BENEFIT COPY - why the rider NEEDS this upsell ───────────────────────
  // Keyed by the UPSELL product's inferred type.
  // Shown as a bold pitch line under the product name in the widget.
  const BENEFIT_COPY = {
    // Helmet ecosystem
    'bluetooth intercom': '📞 Add calls, music & GPS - hands-free, head-up',
    'intercom':           '📞 Rider-to-rider comms up to 1km',
    'visor':              '👁️ Crystal optics - no glare, no distortion',
    'helmet':             '🪖 Full head protection - ECE certified',
    'helmet bag':         '🎒 Keeps your ₹6,000 helmet scratch-free',
    // Ignition & service
    'spark plug':         '⚡ Faster ignition & up to 20% better fuel burn',
    'iridium plug':       '⚡ Precision ignition - sharper throttle response',
    'air filter':         '💨 +15–20% airflow - more pull from every gear',
    'engine oil':         '🛡️ Engine protection at every RPM, every ride',
    'oil filter':         '🔧 Cleaner oil = longer engine life',
    'gear oil':           '⚙️ Smoother gearbox shifts, reduced wear',
    'brake fluid':        '🛑 Fresh fluid prevents brake fade under hard braking',
    'fork oil':           '🏍️ Restored suspension feel - sharper cornering',
    // Brakes
    'brake pad':          '🛑 Sharper bite - shorter stopping distance',
    'brake disc':         '🛑 New pads need clean rotors to work as designed',
    'brake shoe':         '🛑 Complete the drum brake service',
    'brake caliper':      '🛑 Full caliper refresh - consistent braking force',
    // Drivetrain
    'chain':              '⚙️ Smooth power transfer, zero slack',
    'sprocket kit':       '⚙️ Better acceleration response from every gear',
    'chain lube':         '⚙️ Extends chain life by up to 3×',
    'chain cleaner':      '🔧 Clean chain = better lube penetration',
    'clutch plate':       '⚙️ Crisp clutch engagement, no slip',
    'clutch cable':       '🔧 Light lever feel - eliminate cable stretch',
    'brake cable':        '🔧 Consistent brake lever response',
    // Tyres
    'tyre':               '🔄 Better contact patch - more grip in corners',
    'tube':               '🔧 Complete the tyre fitment properly',
    // Lighting
    'fog light':          '💡 2× road visibility - ride nights fearlessly',
    'auxiliary light':    '💡 Illuminate hazards before they reach you',
    'led pod':            '💡 4800+ lumens - cut through darkness',
    'led headlight':      '💡 3× brighter than stock - zero blind spots',
    'headlight':          '💡 Daylight-bright beam - seen and see further',
    'indicator':          '👁️ Brighter turn signals - seen in direct sunlight',
    'tail light':         '👁️ High-vis brake light - cars see you sooner',
    'led tail light':     '👁️ Instant-on LED - rear visibility upgrade',
    'led kit':            '💡 Full LED conversion - front to rear',
    // Hazard & electrical
    'hazard module':      '⚡ 60 flash patterns - hazard + integrated tail sync',
    'flash module':       '⚡ Smarter blinkers - turn signal meets hazard mode',
    'switch kit':         '🔌 Plug & play control - no wiring mess, IP-67 sealed',
    'dimmer':             '🔆 4-level brightness control - city to highway in one click',
    'wiring harness':     '🔌 Clean relay-protected install - lights power on right',
    'battery':            '⚡ Reliable cold starts - every morning, every season',
    // Guards & protection
    'crash guard':        '🛡️ Protects engine and frame in a lowside',
    'engine guard':       '🛡️ Guards your sump - cheap insurance on any terrain',
    'frame slider':       '🛡️ Limits damage in a slide - saves your fairings',
    'saree guard':        '🛡️ Prevents fabric from hitting the chain',
    'sump guard':         '🛡️ Belly protection on trails and rough roads',
    'tank guard':         '🛡️ Shields your tank from bag buckles & knees',
    'tank pad':           '🛡️ No more knee scratches on your paint',
    'seat cover':         '🏍️ Grip surface + comfort on long hauls',
    // Ergonomics
    'handlebar':          '🏍️ Better leverage - precise steering input',
    'grip set':           '🤝 Better grip = better control, always',
    'bar end':            '🏍️ Kills handlebar buzz - less fatigue on long rides',
    'mirror':             '👁️ Full rear visibility - wider field, less blind spot',
    'lever set':          '✋ Adjustable reach - fits your hand, not the other way',
    // Suspension
    'suspension':         '🏍️ Already upgrading suspension? Complete the build',
    'fork':               '🔧 Fresh fork oil completes the suspension service',
    // Phone & mounts
    'phone holder':       '📱 Lock your phone in - navigation & calls on the go',
    'phone mount':        '📱 Secure mount - no rattles, no drops',
    // Windscreen
    'windscreen':         '💨 Cuts wind blast - less fatigue on highway runs',
    'windshield':         '💨 Reduces turbulence - cleaner ride at speed',
    // Radiator
    'radiator guard':     '🛡️ Protects your radiator from debris & stones',
    // Footpegs
    'fuel tuner':         '🔥 Full tune needs full bolt-ons - exhaust + filter first',
    'ecu':                '🔥 ECU tune unlocks full gains only with free-flow exhaust',
    'rearset':            '🦶 Adjustable position - your riding, your ergonomics',
    // Exhaust
    'exhaust':            '🔊 Better flow, stronger pull through the rev range',
    'silencer':           '🔊 Tuned note - sounds right, performs better',
    'muffler':            '🔊 Improved exhaust scavenging - more power out back',
  };

  function getBenefit(upsellProduct) {
    const pt = inferTypeFromTitle(upsellProduct.title) || lc(upsellProduct.product_type);
    return BENEFIT_COPY[pt] || null;
  }

  const HEADINGS = [
    'Make It a Full Kit', 'Complete the Build', 'Build the Full Setup',
    'Unleash the Full Kit', 'Frequently Bought Together',
  ];
  const SOCIAL_COPY = [
    'enthusiasts built this combo this week', 'enthusiasts upgraded with this',
    'enthusiasts who bought this also got it', 'enthusiasts went full kit',
  ];

  // ── TRIPLE REWARD ───────────────────────────────────────────────────────────
  // Store runs "Buy 3 items, get 3% off" (auto-applied at checkout). Surface it
  // in the widget to push the rider to the full 3-item kit. Tune to match the
  // live cart promo.
  const REWARD = { minItems: 3, pct: 3, label: 'Triple Reward' };

  const fmt = (c) => '₹' + Math.round(c / 100).toLocaleString('en-IN');
  // Like fmt but keeps paise when the value isn't a whole rupee (e.g. a 3% cut → ₹3,243.68)
  const money = (c) => {
    const r = c / 100;
    return '₹' + r.toLocaleString('en-IN', { minimumFractionDigits: Number.isInteger(r) ? 0 : 2, maximumFractionDigits: 2 });
  };
  const pick = (a) => a[Math.floor(Math.random() * a.length)];
  const rnd = (a, b) => Math.floor(Math.random() * (b - a + 1)) + a;
  const lc = (s) => (s || '').toLowerCase().trim();

  function inferTypeFromTitle(title) {
    const t = lc(title);
    for (const [type, kws] of TITLE_TYPES) {
      if (kws.some(k => t.includes(k))) return type;
    }
    return '';
  }

  function sigWords(s) {
    return lc(s).split(/[\W_]+/).filter(w => w.length > 3 && !/^\d+$/.test(w));
  }

  // Single-word terminal signals - if BOTH titles share this word, it's same category.
  // Catches "AXOR X-Cross Helmet" vs "SMK Retro Seven Helmet" without needing 'face'.
  const TERMINAL_WORDS = new Set(['helmet', 'exhaust', 'silencer', 'muffler', 'battery', 'tyre', 'suspension', 'sprocket']);

  function isSameProductFamily(titleA, titleB, typeA, typeB) {
    // Gate A: same terminal type (exhaust vs exhaust, helmet vs helmet)
    if (typeA && typeB && typeA === typeB && TERMINAL_TYPES.has(typeA)) return true;

    const wa = sigWords(titleA), wb = sigWords(titleB);
    if (!wa.length || !wb.length) return false;

    // Gate D: single terminal word shared - any two helmets/exhausts/etc
    if (wa.some(w => TERMINAL_WORDS.has(w) && wb.includes(w))) return true;

    const common = wa.filter(w => wb.includes(w));
    const sim = common.length / Math.max(wa.length, wb.length);

    // Gate B: high similarity
    if (sim >= 0.60) return true;

    // Gate C: moderate similarity + shared category phrase pair
    if (sim >= 0.30) {
      const sharedPhrase = CATEGORY_PAIRS.some(([w1, w2]) =>
        wa.includes(w1) && wa.includes(w2) && wb.includes(w1) && wb.includes(w2)
      );
      if (sharedPhrase) return true;
    }
    return false;
  }

  function getComplements(type) {
    const t = lc(type);
    if (!t) return [];
    if (COMPLEMENT[t]) return COMPLEMENT[t];
    for (const [k, v] of Object.entries(COMPLEMENT)) {
      if (t.includes(k) || k.includes(t)) return v;
    }
    return [];
  }

  // ── CAR AWARENESS ───────────────────────────────────────────────────────────
  // The catalog has no car MODEL data the engine can read, and car parts
  // (filters, plugs, body kits) are model-specific. So on car pages we only
  // auto-upsell fitment-SAFE universal items; model-specific styling/service
  // parts are left to manual pins + the Shopify-recommendation fallback.
  const CAR_UNIVERSAL_COMPLEMENTS = [
    'engine oil', 'coolant', 'additive', 'fuel optimizer',
    'engine treatment', 'brake fluid', 'car care', 'cleaner',
  ];
  // Type/title hints that mark a product as car-only (used when the
  // 'vehicle-car' tag is absent).
  const CAR_HINT_RE = /\bcar\b|body kit|bumper|spoiler|diffuser|side skirt|window louver|armrest|gear knob|steering wheel|paddle shifter|cold air intake/;
  const BIKE_HINT_RE = /motorcycle|scooter|\bbike\b|helmet/;

  function isCarProduct(data) {
    const tags = (Array.isArray(data.productTags) ? data.productTags : []).map(lc);
    if (tags.includes('vehicle-car')) return true;
    if (tags.includes('vehicle-bike')) return false;
    const t = lc(data.productType) + ' ' + lc(data.productTitle || '');
    return CAR_HINT_RE.test(t) && !BIKE_HINT_RE.test(t);
  }

  // 'car' | 'bike' | '' - used to hard-block cross-vehicle upsells
  // (e.g. a JASO-MA2 motorcycle oil must never show on a car cold-air-intake).
  function vehicleClassOf(tags, title) {
    const tg = (Array.isArray(tags) ? tags : String(tags || '').split(', ')).map(lc);
    if (tg.includes('vehicle-car'))  return 'car';
    if (tg.includes('vehicle-bike')) return 'bike';
    const t = lc(title);
    if (CAR_HINT_RE.test(t) && !BIKE_HINT_RE.test(t)) return 'car';
    if (BIKE_HINT_RE.test(t)) return 'bike';
    return '';
  }

  // Performance/tuning buyer? (cold air intake, exhaust, ECU, intake, tuner…)
  const PERF_RE = /cold air intake|\bintake\b|induction|performance|exhaust|\becu\b|tuner|fuel optimizer|sprocket|suspension/;
  function isPerformance(data) {
    const tags = (Array.isArray(data.productTags) ? data.productTags : []).map(lc).join(' ');
    const t = lc(data.productType) + ' ' + lc(data.productTitle || '') + ' ' + tags;
    return PERF_RE.test(t);
  }
  // Car performance buyers get tuning/care universal items first (fitment-safe);
  // the model-specific stuff is handled by the collection CTA, not auto-picks.
  const CAR_PERF_COMPLEMENTS = [
    'fuel optimizer', 'additive', 'engine treatment', 'coolant', 'car care', 'cleaner',
  ];

  // Effective complement list for a product, honouring car fitment + intent.
  function getComplementsFor(data) {
    const curType = lc(data.productType) || inferTypeFromTitle(data.productTitle || '');
    if (isCarProduct(data)) {
      return isPerformance(data) ? CAR_PERF_COMPLEMENTS : CAR_UNIVERSAL_COMPLEMENTS;
    }
    return getComplements(curType);  // bikes: existing map is already intent-aware
  }

  // ── COLLECTION PUSH ─────────────────────────────────────────────────────────
  // Map a product to the most relevant collection to surface as a
  // "Explore more in …" CTA. Handles verified live on the store.
  const LIGHT_RE     = /fog|headlight|tail light|indicator|\bled\b|switch kit|wiring|hazard|lamp|lighting/;
  const PROTECT_RE   = /guard|slider|bash plate|protector|radiator|sump/;
  const TOURING_RE   = /luggage|top box|side box|top case|carrier|backrest|saddle|phone mount|gps|navigation|paddock|stand|mount/;
  const HELMET_RE    = /helmet|visor|intercom|headset|riding jacket|hydration/;
  function collectionFor(data) {
    const klass = vehicleClassOf(data.productTags, data.productTitle);
    const t = lc(data.productType) + ' ' + lc(data.productTitle || '');
    if (klass === 'car' || isCarProduct(data)) {
      // Car air filter / intake itself → broader performance category (no self-loop).
      if (/air filter|intake/.test(t)) return { handle: 'car-performance-upgrades', label: 'Car Performance Upgrades' };
      if (isPerformance(data)) return { handle: 'car-performance-upgrades', label: 'Car Performance Upgrades' };
      // Ignition/plug: the #1 complement (air filter) is model-specific with many
      // options - so point the rider to pick their fit instead of guessing one.
      if (/spark plug|\bplug\b|ignition/.test(t)) return { handle: 'car-air-filters-intakes', label: 'performance air filter', cta: 'Find your' };
      return { handle: 'shop-by-car-premium-automotive-accessories', label: 'Shop by Car' };
    }
    // Bike
    if (HELMET_RE.test(t))   return { handle: 'rider-essentials', label: 'Rider Essentials' };
    if (LIGHT_RE.test(t))    return { handle: 'lighting-visibility', label: 'Lighting & Visibility' };
    if (PROTECT_RE.test(t))  return { handle: 'bike-protection', label: 'Protection & Guards' };
    if (TOURING_RE.test(t))  return { handle: 'touring-mounts', label: 'Touring & Mounts' };
    return { handle: 'performance-parts', label: 'Performance Parts' };
  }

  function vehicleTagsFrom(tags) {
    return (Array.isArray(tags) ? tags : []).filter(t =>
      VEHICLE_PATTERNS.some(p => lc(t).includes(p))
    );
  }

  // Detect bike models mentioned in a product TITLE when Shopify tags are absent.
  // "MotoCare Visor for Royal Enfield Guerrilla 450" → ['royal', 'enfield', 'guerrilla']
  function vehicleNamesInTitle(title) {
    const t = lc(title);
    return VEHICLE_PATTERNS.filter(p => t.includes(p.replace(/-/g, ' ')));
  }

  function getSelectedVariant() {
    return (
      new URLSearchParams(window.location.search).get('variant') ||
      document.querySelector('form[action*="/cart/add"] input[name="id"]')?.value ||
      document.querySelector('form[action*="/cart/add"] select[name="id"]')?.value ||
      String(window.RTUpsellData?.productFirstVariantId || '')
    );
  }

  const suggestToCents = (v) =>
    Math.round(parseFloat(String(v || 0).replace(/,/g, '')) * 100);

  // /products/{handle}.js returns prices already in paise (integers). Keep them
  // as-is; only convert if a rupee-looking value slips through. Shared by the
  // L0 manual-upsell path and fetchHandlePicks so both agree.
  const toPaise = (val) =>
    (val > 1000 && Number.isInteger(val)) ? val : Math.round(parseFloat(val) * 100);

  // HTML-escape for values interpolated into innerHTML from live product JSON.
  const esc = (s) => String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

  function normSuggest(p) {
    return {
      id: p.id, handle: p.handle || '', title: p.title || '', vendor: p.vendor || '',
      product_type: p.type || p.product_type || '',
      featured_image: p.featured_image?.url ? { src: p.featured_image.url } : null,
      tags: Array.isArray(p.tags) ? p.tags : String(p.tags || '').split(', '),
      variants: [{
        id: p.variants?.[0]?.id || p.id,
        price: suggestToCents(p.price),
        compare_at_price: p.compare_at_price_max
          ? suggestToCents(p.compare_at_price_max) : null,
      }],
    };
  }

  function scoreOne(p, data) {
    const curTitle = data.productTitle || '';
    const curType  = lc(data.productType) || inferTypeFromTitle(curTitle);
    const complements = getComplementsFor(data);
    const vTags = vehicleTagsFrom(data.productTags);

    // Title-first: inferTypeFromTitle is normalised; product_type field is raw/inconsistent
    // e.g. Shopify may store "Iridium Plug" for one NGK and "Spark Plug" for another
    const pt = inferTypeFromTitle(p.title) || lc(p.product_type);

    if (isSameProductFamily(curTitle, p.title, curType, pt)) {
      return { score: -100, complementHit: false, sameFamily: true };
    }

    // Hard car↔bike guard - never upsell across vehicle classes.
    const curClass  = vehicleClassOf(data.productTags, curTitle);
    const candClass = vehicleClassOf(p.tags, p.title);
    if (curClass && candClass && curClass !== candClass) {
      return { score: -100, complementHit: false, sameFamily: false, vehMismatch: true };
    }

    let s = 0;
    let complementHit = false;

    const tags = Array.isArray(p.tags)
      ? p.tags.map(lc)
      : String(p.tags || '').toLowerCase().split(', ');

    complements.forEach((c, i) => {
      // Guard: pt must be non-empty - c.includes('') is always true in JS
      if (pt && (pt.includes(c) || c.includes(pt))) {
        s += Math.max(10 - i * 2, 2);
        complementHit = true;
      }
    });

    // Penalise if NO complement matches - prevents vehicle-tag-only wins
    if (!complementHit) s -= 15;

    // Vehicle tag bonus
    vTags.forEach(vt => { if (tags.includes(lc(vt))) s += 3; });

    // ── Cross-bike penalty ────────────────────────────────────────────────
    // Requires MODEL-level tag overlap, not just brand overlap.
    // "Honda CB200X guard" + "Honda CB350 suspension" both have 'honda'
    // but are incompatible - only model tags ('cb350' vs 'cb200x') decide fitment.
    const candidateVTags = vehicleTagsFrom(tags);
    const candidateTitleBikes = candidateVTags.length === 0
      ? vehicleNamesInTitle(p.title) : [];
    const candidateIsBikeSpecific = candidateVTags.length > 0 || candidateTitleBikes.length > 0;

    if (candidateIsBikeSpecific) {
      if (vTags.length > 0) {
        // Strip brand-only tags - model tags must match
        const modelVTags = vTags.filter(t => !BRAND_TAGS.has(lc(t)));
        const candidateModelVTags = candidateVTags.filter(t => !BRAND_TAGS.has(lc(t)));

        let hasOverlap;
        if (modelVTags.length > 0 && candidateModelVTags.length > 0) {
          // Both have model-specific tags - require model match
          hasOverlap = modelVTags.some(vt =>
            candidateModelVTags.some(cvt =>
              lc(cvt).includes(lc(vt)) || lc(vt).includes(lc(cvt))
            )
          );
        } else {
          // One or both lack model tags (brand-only or universal) - use brand-level
          hasOverlap = vTags.some(vt =>
            candidateVTags.some(cvt =>
              lc(cvt).includes(lc(vt)) || lc(vt).includes(lc(cvt))
            )
          );
        }
        if (!hasOverlap) s -= 20;
      } else {
        s -= 20; // universal current product + bike-specific candidate
      }
    }

    // ── Title-model mismatch penalty ─────────────────────────────────────
    // If the candidate title explicitly names a model (CB200X) that doesn't
    // match the current product's model tags (CB350) → wrong fitment.
    // This catches vendors who over-tag products for multiple bikes.
    const titleBikesInCandidate = vehicleNamesInTitle(p.title);
    if (titleBikesInCandidate.length > 0) {
      const modelTitleBikes = titleBikesInCandidate.filter(b => !BRAND_TAGS.has(b));
      if (modelTitleBikes.length > 0 && vTags.length > 0) {
        const curModelTags = vTags.filter(t => !BRAND_TAGS.has(lc(t)));
        if (curModelTags.length > 0) {
          const titleMatch = modelTitleBikes.some(tb =>
            curModelTags.some(ct => ct.includes(tb) || tb.includes(ct))
          );
          if (!titleMatch) s -= 20; // title says different model
        }
      }
    }

    // Same type penalty
    if (pt && curType && pt === curType) s -= 50;

    // Has discount = clearer value prop
    const v = p.variants?.[0] || {};
    if (v.compare_at_price && parseInt(v.compare_at_price) > parseInt(v.price)) s += 2;

    if (DEBUG) console.warn(`[RT] score ${s} (hit:${complementHit}): "${p.title}" type:${pt}`);
    // Store complementHit on the scored object for tiered filtering
    return { score: s, complementHit, sameFamily: false };
  }

  // ── COLLECT ALL CANDIDATES ─────────────────────────────────────────────────
  async function fetchProducts(url, isSuggest) {
    try {
      const r = await fetch(url);
      if (!r.ok) return [];
      const json = await r.json();
      const raw = json.products || json.resources?.results?.products || [];
      return isSuggest ? raw.map(normSuggest) : raw;
    } catch (_) { return []; }
  }

  async function findUpsells(data, wantCount = 2) {
    const { productId, productType, productTags, productVendor, productTitle } = data;
    const curType     = lc(productType) || inferTypeFromTitle(productTitle || '');
    const isCar       = isCarProduct(data);
    const complements = getComplementsFor(data);
    const vTags       = vehicleTagsFrom(productTags || []);

    // Use most specific model tag for search - prefer model over brand
    // vTags[0] might be 'honda' (brand), but 'cb350' is more useful as a search term
    const modelTags = vTags.filter(t => !BRAND_TAGS.has(lc(t)));
    const searchTag = modelTags.length > 0 ? modelTags[0] : (vTags[0] || '');

    if (DEBUG) console.warn(`[RT] curType="${curType}" complements:`, complements, 'vTags:', vTags, 'searchTag:', searchTag);

    // ── L0: Manual upsell override (data-upsell attribute) ───────────────────
    if (data.manualUpsellEntries?.length) {
      if (DEBUG) console.warn('[RT] L0 entries:', data.manualUpsellEntries);
      try {
        const fetches = data.manualUpsellEntries.map(async entry => {
          try {
            if (entry.startsWith('handle:')) {
              const handle = entry.slice(7);
              const r = await fetch(`/products/${handle}.js`);
              if (r.ok) return await r.json();
            }
            // id: prefix or raw numeric - use recommendations as lookup
            // (products.json?ids= doesn't work on Shopify AJAX API)
            return null;
          } catch (_) { return null; }
        });
        const resolved = (await Promise.all(fetches)).filter(Boolean);
        if (DEBUG) console.warn('[RT] L0 resolved:', resolved.map(p => p.title));
        if (resolved.length) {
          const picks = [], usedTypes = new Set();
          for (const p of resolved) {
            if (picks.length >= 2) break;
            const pt = inferTypeFromTitle(p.title) || lc(p.product_type);
            if (pt && usedTypes.has(pt)) continue;
            // Normalize to standard product shape
            const v = p.variants?.[0] || {};
            picks.push({
              ...p,
              _score: 999,
              featured_image: p.featured_image || (p.images?.[0] ? { src: p.images[0].src } : null),
              variants: [{ id: v.id, price: toPaise(v.price), compare_at_price: v.compare_at_price ? toPaise(v.compare_at_price) : null }]
            });
            if (pt) usedTypes.add(pt);
          }
          if (picks.length) return picks;
        }
      } catch (e) { console.warn('[RT] L0 error:', e); }
    }

    // Collect candidates from every layer simultaneously for best coverage
    const layers = [];

    // L1: complementary intent
    layers.push(fetchProducts(
      `/recommendations/products.json?product_id=${productId}&limit=6&intent=complementary`,
      false
    ));

    // L2: model tag + each complement (up to first 3)
    for (let i = 0; i < Math.min(complements.length, 3) && searchTag; i++) {
      const q = encodeURIComponent(`${searchTag.replace(/-/g, ' ')} ${complements[i]}`);
      layers.push(fetchProducts(
        `/search/suggest.json?q=${q}&resources[type]=product&resources[limit]=6`,
        true
      ));
    }

    // L3: vendor + first complement only (no vendor-only search - too noisy)
    if (productVendor && complements.length) {
      layers.push(fetchProducts(
        `/search/suggest.json?q=${encodeURIComponent(`${productVendor} ${complements[0]}`)}&resources[type]=product&resources[limit]=5`,
        true
      ));
    }

    // L4: default related
    layers.push(fetchProducts(
      `/recommendations/products.json?product_id=${productId}&limit=8`,
      false
    ));

    // L5: complement-only - search ALL complement terms for widest coverage
    for (let i = 0; i < complements.length; i++) {
      layers.push(fetchProducts(
        `/search/suggest.json?q=${encodeURIComponent(complements[i])}&resources[type]=product&resources[limit]=6`,
        true
      ));
    }

    // L6: model-only search (last resort)
    if (searchTag) {
      layers.push(fetchProducts(
        `/search/suggest.json?q=${encodeURIComponent(searchTag.replace(/-/g, ' '))}&resources[type]=product&resources[limit]=12`,
        true
      ));
    }

    // L6b: if model tag exists, try model + each remaining complement for wider coverage
    if (searchTag && complements.length > 3) {
      for (let i = 3; i < Math.min(complements.length, 5); i++) {
        const q = encodeURIComponent(`${searchTag.replace(/-/g, ' ')} ${complements[i]}`);
        layers.push(fetchProducts(
          `/search/suggest.json?q=${q}&resources[type]=product&resources[limit]=6`,
          true
        ));
      }
    }

    // Wait for all layers
    const allResults = await Promise.all(layers);

    // Merge, score, deduplicate
    const seen = new Map();
    const curId = String(data.productId);

    allResults.flat().forEach(p => {
      const id = String(p.id);
      if (id === curId) return;
      const result = scoreOne(p, data);
      const s = result.score;
      const ch = result.complementHit;
      if (!seen.has(id) || seen.get(id)._score < s) {
        seen.set(id, { ...p, _score: s, _complementHit: ch, _sameFamily: result.sameFamily, _vehMismatch: result.vehMismatch });
      }
    });

    // Shopify's own "frequently bought together" set (L1) - store-curated,
    // used as the safe universal fallback when our rules underfill.
    const recIds = new Set((allResults[0] || []).map(p => String(p.id)));

    const allScored = [...seen.values()].sort((a, b) => b._score - a._score);

    if (DEBUG) console.warn('[RT] All scored:', allScored.slice(0, 8).map(p => `${p.title} (${p._score}, hit:${p._complementHit})`));

    // ── Tiered fallback ──────────────────────────────────────────────────────
    // RULE: _complementHit is always required - never show irrelevant products
    // Tier 1: complement hit + good score (right bike or universal)
    // Tier 2: complement hit + universal product (fills slots Tier 1 missed)
    // Tier 3: complement hit + universal, any score (absolute last resort)

    function isUniversal(p) {
      const tags = Array.isArray(p.tags) ? p.tags.map(lc) : String(p.tags||'').toLowerCase().split(', ');
      const hasModelTag = vehicleTagsFrom(tags).some(t => !BRAND_TAGS.has(lc(t)));
      const hasModelInTitle = vehicleNamesInTitle(p.title).some(b => !BRAND_TAGS.has(b));
      return !hasModelTag && !hasModelInTitle;
    }

    function pickBest(pool, count) {
      const picks = [], usedTypes = new Set();
      for (const p of pool) {
        if (picks.length >= count) break;
        const pt = inferTypeFromTitle(p.title) || lc(p.product_type);
        if (pt && usedTypes.has(pt)) continue;
        picks.push(p);
        if (pt) usedTypes.add(pt);
      }
      return picks;
    }

    // Tier 1: complement hit + positive score
    let picks = pickBest(
      allScored.filter(p => p._complementHit && p._score > 0),
      wantCount
    );
    if (DEBUG) console.warn(`[RT] Tier 1 (${picks.length}):`, picks.map(p => p.title));

    // Tier 2: fill with universal complement products (score can be negative from bike mismatch)
    if (picks.length < wantCount) {
      const usedIds = new Set(picks.map(p => String(p.id)));
      const extra = pickBest(
        allScored.filter(p => !usedIds.has(String(p.id)) && p._complementHit && isUniversal(p)),
        wantCount - picks.length
      );
      picks = [...picks, ...extra];
      if (DEBUG) console.warn(`[RT] Tier 2 fills (${extra.length}):`, extra.map(p => p.title));
    }

    // ── Tier 3: safe universal fallback ───────────────────────────────────────
    // Guarantees coverage. Uses Shopify's store-curated "complementary"
    // recommendations (L1) only - no complementHit required - but still drops
    // same-family items and, for bikes, wrong-model items. This is what makes
    // previously-empty products (coolant, body kit, navigation, etc.) show
    // something relevant instead of nothing.
    if (picks.length < wantCount) {
      const usedIds = new Set(picks.map(p => String(p.id)));
      const fallbackPool = allScored.filter(p =>
        !usedIds.has(String(p.id)) &&
        recIds.has(String(p.id)) &&        // only Shopify's complementary set
        !p._sameFamily &&                  // never the same product family
        !p._vehMismatch &&                 // never cross car↔bike
        (isCar || isUniversal(p) || p._score > -20)  // bikes: skip clear wrong-model
      );
      const extra = pickBest(fallbackPool, wantCount - picks.length);
      picks = [...picks, ...extra];
      if (DEBUG) console.warn(`[RT] Tier 3 fallback (${extra.length}):`, extra.map(p => p.title));
    }

    return picks;
  }

  // ── RENDER ─────────────────────────────────────────────────────────────────
  function render(upsells, d, collection) {
    const root = document.getElementById('rt-smart-upsell-root');
    if (!root) return;
    if (!upsells.length && !collection) return;  // nothing to show → caller collapses
    root.classList.remove('rt-up-empty');

    const curP = d.productPrice;
    const ups  = upsells.map(u => {
      const v   = u.variants?.[0] || {};
      const p   = parseInt(v.price) || 0;
      const cp  = parseInt(v.compare_at_price) || 0;
      return { ...u, _p: p, _cp: cp, _save: cp > p ? cp - p : 0, _vid: v.id };
    });

    const totalSave = ups.reduce((a, u) => a + u._save, 0);
    const totalP    = curP + ups.reduce((a, u) => a + u._p, 0);
    const itemCount = 1 + ups.length;

    const upsellRow = (u, idx) => {
      const benefit = getBenefit(u);
      const imgSrc =
        u.featured_image?.src ||
        u.featured_image?.url ||
        (typeof u.featured_image === 'string' ? u.featured_image : '') ||
        u.images?.[0]?.src || '';
      const productUrl = `/products/${u.handle}`;
      return `
      <div class="rt-up__row rt-up__row--add" id="rt-up-addon-${idx}">
        <label class="rt-up__chk-label" onclick="event.stopPropagation()">
          <input type="checkbox" id="rt-up-cb-${idx}" class="rt-up__cb-hidden rt-up__cb" data-idx="${idx}" checked>
          <span class="rt-up__chk rt-up__chk--on" id="rt-up-chk-${idx}">
            <svg width="8" height="8" viewBox="0 0 8 8" fill="none"><path d="M1 4l2 2 4-4" stroke="#fff" stroke-width="1.6" stroke-linecap="round"/></svg>
          </span>
        </label>
        <a href="${productUrl}" class="rt-up__product-link" data-handle="${u.handle}" onclick="event.stopPropagation()">
          ${imgSrc
            ? `<img class="rt-up__img" src="${rtImg(imgSrc, 96)}" alt="${esc(u.title)}" loading="lazy" onerror="this.outerHTML='<div class=\\'rt-up__img rt-up__img--ph\\'></div>'">`
            : `<div class="rt-up__img rt-up__img--ph"></div>`}
        </a>
        <div class="rt-up__info rt-up__info--link" data-product-url="${productUrl}">
          <span class="rt-up__name">${esc(u.title)}</span>
          ${benefit ? `<span class="rt-up__benefit">${benefit}</span>` : ''}
          <div class="rt-up__meta">
            ${u._save > 0 ? `<span class="rt-up__pill">SAVE ${fmt(u._save)}</span>` : ''}
            <span class="rt-up__sub">${esc(u.vendor || '')}</span>
          </div>
        </div>
        <div class="rt-up__pricestack">
          <span class="rt-up__price">${fmt(u._p)}</span>
          ${u._save > 0 ? `<span class="rt-up__was">${fmt(u._cp)}</span>` : ''}
        </div>
      </div>`;
    };

    const hasBundle = ups.length > 0;

    const headHTML = `
  <div class="rt-up__head">
    <span class="rt-up__dot"></span>
    <strong class="rt-up__title">${(hasBundle ? pick(HEADINGS) : 'Complete the build').toUpperCase()}</strong>
    <span class="rt-up__social">
      <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true">
        <circle cx="5.5" cy="5.5" r="5" stroke="#22c55e" stroke-width="1.2"/>
        <path d="M2.5 5.5l2.3 2.3L9 3" stroke="#22c55e" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <span>${rnd(28,97)} ${pick(SOCIAL_COPY)}</span>
    </span>
  </div>`;

    const anchorHTML = `
    <div class="rt-up__row">
      <span class="rt-up__chk rt-up__chk--on rt-up__chk--lock">
        <svg width="8" height="8" viewBox="0 0 8 8" fill="none"><path d="M1 4l2 2 4-4" stroke="#fff" stroke-width="1.6" stroke-linecap="round"/></svg>
      </span>
      <img class="rt-up__img" src="${rtImg(d.productImage, 96)}" alt="" loading="lazy" onerror="this.outerHTML='<div class=\\'rt-up__img rt-up__img--ph\\'></div>'">
      <div class="rt-up__info">
        <span class="rt-up__name">${esc(d.productTitle)}</span>
        <span class="rt-up__sub">${d.productVendor ? esc(d.productVendor) + ' · ' : ''}This product</span>
      </div>
      <span class="rt-up__price">${fmt(curP)}</span>
    </div>`;

    // Collection CTA - "Explore more in …" link (hybrid). Solo variant is
    // the prominent browse card shown when there's no confident bundle.
    const browseVerb = (collection && collection.cta) || (hasBundle ? 'Explore more in' : 'Browse');
    const browseHTML = collection ? `
  <a class="rt-up__browse${hasBundle ? '' : ' rt-up__browse--solo'}" href="/collections/${collection.handle}">
    <span class="rt-up__browse-txt">${browseVerb} <strong>${collection.label}</strong></span>
    <span class="rt-up__browse-arrow" aria-hidden="true">→</span>
  </a>` : '';

    const bundleHTML = `
  <div class="rt-up__items">
    ${anchorHTML}
    ${ups.map((u, i) => upsellRow(u, i)).join('')}
  </div>

  ${totalSave > 0 ? `
  <div class="rt-up__save-row">
    <div class="rt-up__save-left">
      <div class="rt-up__save-lbl">YOU SAVE</div>
      <div class="rt-up__save-amt" id="rt-up-save-amt">${fmt(totalSave)}</div>
    </div>
    <div class="rt-up__save-right">
      <div class="rt-up__save-track"><div class="rt-up__save-bar"></div></div>
      <span class="rt-up__save-note">Great deal! You're saving on this kit.</span>
    </div>
  </div>` : ''}

  <div class="rt-up__reward" id="rt-up-reward"></div>

  <div class="rt-up__foot">
    <div class="rt-up__tot-wrap">
      <div class="rt-up__tot-lbl">BUNDLE TOTAL (<span id="rt-up-cnt">${itemCount}</span> ITEMS)</div>
      <div class="rt-up__tot-price" id="rt-up-tot">${fmt(totalP)}</div>
    </div>
    <button class="rt-up__btn" id="rt-up-btn">Add ${itemCount} items to cart</button>
  </div>
  ${browseHTML}`;

    const soloHTML = `
  <div class="rt-up__items">
    ${anchorHTML}
  </div>
  ${browseHTML}`;

    root.innerHTML = `
<div class="rt-up${hasBundle ? '' : ' rt-up--solo'}">
  ${headHTML}
  ${hasBundle ? bundleHTML : soloHTML}
</div>`;

    // Fade in the new content smoothly
    const rtUpEl = root.querySelector('.rt-up');
    if (rtUpEl) {
      rtUpEl.style.opacity = '0';
      rtUpEl.style.transform = 'translateY(4px)';
      rtUpEl.style.transition = 'none';
      // Force reflow so transition triggers
      void rtUpEl.offsetHeight;
      rtUpEl.style.transition = 'opacity 0.14s ease, transform 0.14s ease';
      requestAnimationFrame(function() {
        rtUpEl.style.opacity = '1';
        rtUpEl.style.transform = 'translateY(0)';
      });
    }

    // ── Conflict shield ───────────────────────────────────────────────────────
    // The theme's custom-async.js binds a delegated `openUpsell` click handler
    // to an ancestor <div>; it throws (null.querySelectorAll) on clicks inside
    // our widget and was swallowing the click. Stop our clicks from bubbling
    // out to it. Guarded so re-renders don't stack listeners.
    if (!root.dataset.rtClickShield) {
      root.addEventListener('click', function (e) { e.stopPropagation(); });
      root.dataset.rtClickShield = '1';
    }

    // Collection CTA - navigate explicitly (don't rely on default <a> nav,
    // which the theme/extension handlers can intercept). Works in both modes.
    const browseLink = root.querySelector('.rt-up__browse');
    if (browseLink) {
      browseLink.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        window.location.assign(browseLink.getAttribute('href'));
      });
    }

    // Solo (collection-only) card has no bundle controls - nothing to wire up.
    if (!ups.length) return;

    // ── Interactions ──────────────────────────────────────────────────────────
    const btn       = document.getElementById('rt-up-btn');
    const totEl     = document.getElementById('rt-up-tot');
    const cntEl     = document.getElementById('rt-up-cnt');
    const saveAmtEl = document.getElementById('rt-up-save-amt');
    const rewardEl  = document.getElementById('rt-up-reward');
    const REWARD_CHECK = '<svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true"><circle cx="6.5" cy="6.5" r="6" fill="currentColor" opacity=".18"/><path d="M3.6 6.6l2 2 3.8-4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    const REWARD_INFO  = '<svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true"><circle cx="6.5" cy="6.5" r="5.6" stroke="currentColor" stroke-width="1.3"/><path d="M6.5 3.6v3.4M6.5 9.2h.01" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';

    function recalc() {
      let total = curP, count = 1, save = 0;
      ups.forEach((u, i) => {
        const cb = document.getElementById(`rt-up-cb-${i}`);
        if (cb?.checked) {
          total += u._p;
          save  += u._save;
          count++;
        }
      });

      const unlocked  = count >= REWARD.minItems;          // Triple Reward active?
      const rewardCut = unlocked ? Math.round(total * REWARD.pct / 100) : 0;
      const payNow    = total - rewardCut;                 // actual price after the 3%

      // Bundle total = what they actually pay. When unlocked, show the
      // discounted figure (matches the cart) with the pre-discount struck out.
      totEl.innerHTML = unlocked
        ? money(payNow) + ' <s class="rt-up__tot-was">' + money(total) + '</s>'
        : money(total);
      cntEl.textContent = count;
      btn.textContent   = `Add ${count} item${count > 1 ? 's' : ''} to cart`;
      if (saveAmtEl) saveAmtEl.textContent = money(save + rewardCut);

      // Triple Reward strip - no fixed rupee figure (the 3% scales, it's not a cap)
      if (rewardEl) {
        if (unlocked) {
          rewardEl.className = 'rt-up__reward is-unlocked';
          rewardEl.innerHTML = REWARD_CHECK +
            '<span><strong>' + REWARD.pct + '% OFF unlocked</strong> - ' + REWARD.label + ' applied at checkout</span>';
        } else {
          const need = REWARD.minItems - count;
          rewardEl.className = 'rt-up__reward';
          rewardEl.innerHTML = REWARD_INFO +
            '<span>Add <strong>' + need + ' more</strong> to unlock <strong>' + REWARD.pct + '% OFF</strong> - ' + REWARD.label + '</span>';
        }
      }
    }

    recalc();  // initialise reward + totals to match the rendered state

    // Checkbox toggle - only the checkbox label area
    document.querySelectorAll('.rt-up__cb').forEach((cb, i) => {
      cb.addEventListener('change', () => {
        const chk = document.getElementById(`rt-up-chk-${i}`);
        chk?.classList.toggle('rt-up__chk--on', cb.checked);
        // Also dim the row when unchecked
        const row = document.getElementById(`rt-up-addon-${i}`);
        row?.classList.toggle('rt-up__row--unchecked', !cb.checked);
        recalc();
      });
    });

    // Clicking the product info area toggles checkbox (not opens page)
    // Clicking the image/title link opens the product page
    document.querySelectorAll('.rt-up__row--add').forEach((row, i) => {
      // Click anywhere on the row (except checkbox label and product link) toggles checkbox
      row.addEventListener('click', (e) => {
        if (e.target.closest('.rt-up__chk-label') || e.target.closest('.rt-up__product-link')) return;
        const cb = document.getElementById(`rt-up-cb-${i}`);
        if (cb) {
          cb.checked = !cb.checked;
          cb.dispatchEvent(new Event('change'));
        }
      });
    });

    // Add to cart - adds the current product + every checked upsell in ONE
    // call, then sends the rider to /cart. This theme has no cart drawer and
    // no [name="add"] button the old code relied on, so we drive the AJAX
    // Cart API directly and redirect (the expected feedback).
    btn.addEventListener('click', async () => {
      btn.disabled = true;
      btn.textContent = 'Adding…';
      try {
        const items = [];
        const anchorVid = getSelectedVariantId();   // current product's selected variant
        if (anchorVid) items.push({ id: parseInt(anchorVid, 10), quantity: 1 });

        // Resolve each checked upsell to a real VARIANT id. Search-sourced
        // upsells (/search/suggest.json) omit variant data, so _vid can fall
        // back to the PRODUCT id → /cart/add.js 422 "Cannot find variant".
        // Re-fetch the product JSON to get the true first-variant id.
        const chosen = ups.filter((u, i) => document.getElementById(`rt-up-cb-${i}`)?.checked);
        const vids = await Promise.all(chosen.map(async (u) => {
          let vid = u._vid;
          if (!vid || String(vid) === String(u.id)) {
            try {
              const r = await fetch(`/products/${u.handle}.js`);
              if (r.ok) { const pj = await r.json(); vid = pj.variants && pj.variants[0] && pj.variants[0].id; }
            } catch (_) {}
          }
          return vid;
        }));
        vids.forEach((vid) => { if (vid) items.push({ id: parseInt(vid, 10), quantity: 1 }); });

        if (!items.length) { btn.disabled = false; recalc(); return; }

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!res.ok) throw new Error('cart/add failed: ' + res.status);

        btn.textContent = '✓ Added!';
        // Redirect to the cart so the rider sees the full kit.
        window.location.assign('/cart');
      } catch (e) {
        console.warn('[RT Upsell]', e);
        btn.textContent = 'Try again';
        btn.disabled = false;
        setTimeout(recalc, 1500);
      }
    });
  }  // end render

  function refreshCart() {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:open',    { bubbles: true }));
    fetch('/cart.js').then(r => r.json()).then(c => {
      document.querySelectorAll(
        '.cart-count,[data-cart-count],.header__cart-count,.cart__count'
      ).forEach(el => { el.textContent = c.item_count; });
    }).catch(() => {});
  }

  // ── Fetch product handles and return normalized picks ─────────────────────
  async function fetchHandlePicks(entries) {
    const results = await Promise.all(
      entries.map(async entry => {
        if (!entry.startsWith('handle:')) return null;
        const handle = entry.slice(7);
        try {
          const r = await fetch(`/products/${handle}.js`);
          if (!r.ok) return null;
          const p = await r.json();
          const v = p.variants?.[0] || {};
          return {
            ...p,
            _score: 999,
            featured_image: p.featured_image || (p.images?.[0] ? { src: p.images[0].src } : null),
            variants: [{ id: v.id, price: toPaise(v.price), compare_at_price: v.compare_at_price ? toPaise(v.compare_at_price) : null }]
          };
        } catch (_) { return null; }
      })
    );
    return results.filter(Boolean);
  }

  // Get current selected variant title using RTUpsellData.productVariants
  // Xtra updates the URL with ?variant=ID - look it up from the variants array
  function getSelectedVariantTitle() {
    const d = window.RTUpsellData;
    const variants = d?.productVariants || [];

    // Get variant ID from URL
    const vid = new URLSearchParams(window.location.search).get('variant');
    if (vid && variants.length) {
      const match = variants.find(v => String(v.id) === String(vid));
      if (match) return match.title;
    }

    // Fallback: checked radio input in variant selector
    const checked = document.querySelector('.f8pr input[type="radio"]:checked, form[action*="/cart/add"] input[type="radio"]:checked');
    if (checked) {
      const label = checked.closest('label') || document.querySelector(`label[for="${checked.id}"]`);
      if (label) return label.textContent.trim();
    }

    // Fallback: selected option in a select
    const sel = document.querySelector('select[name="id"], .f8pr select');
    if (sel) return sel.options[sel.selectedIndex]?.text?.trim() || '';

    // Fallback: first variant
    return variants[0]?.title || '';
  }

  // ── Match variant ID against variant map ──────────────────────────────────
  // Keys are comma-separated variant IDs (no title lookup needed)
  function matchVariantMap(variantId, variantMap) {
    if (!variantMap || !variantId) return null;
    const vid = String(variantId);
    for (const [keys, handles] of Object.entries(variantMap)) {
      const ids = keys.split(',').map(k => k.trim());
      if (ids.includes(vid)) return handles;
    }
    return null;
  }

  // Get current variant ID - Xtra-aware
  // Xtra does NOT update the URL (?variant=) on variant change (only xclusive theme does).
  // Instead Xtra updates:
  //   1. [data-current-variant] on .f8pr-variant-selection (via AJAX section re-render)
  //   2. input[name="id"].product-variant-id hidden input
  //   3. input[name="variant-id"]:checked radio
  // Priority: DOM attribute > hidden input > URL > first variant fallback
  function getSelectedVariantId() {
    // P1: Xtra's AJAX-updated attribute (most reliable - set after every variant swap)
    const varSel = document.querySelector('[data-current-variant]');
    if (varSel?.dataset?.currentVariant) return String(varSel.dataset.currentVariant);

    // P2: Hidden input Xtra keeps in sync
    const hiddenInput = document.querySelector('input[name="id"].product-variant-id');
    if (hiddenInput?.value) return String(hiddenInput.value);

    // P3: Checked radio button (name="variant-id")
    const checked = document.querySelector('input[name="variant-id"]:checked');
    if (checked?.value) return String(checked.value);

    // P4: URL param (only present on direct deep-link, not after Xtra's AJAX swap)
    const urlVid = new URLSearchParams(window.location.search).get('variant');
    if (urlVid) return urlVid;

    // P5: Any hidden id input in cart form
    const anyInput = document.querySelector('form[action*="/cart/add"] input[name="id"]');
    if (anyInput?.value) return String(anyInput.value);

    return String(window.RTUpsellData?.productFirstVariantId || '');
  }

  async function init() {
    const d = window.RTUpsellData;
    if (!d?.productId) return;

    const root = document.getElementById('rt-smart-upsell-root');
    const upsellAttr = root?.dataset?.upsell || '';
    if (upsellAttr) {
      d.manualUpsellEntries = upsellAttr.split(',').map(s => s.trim()).filter(Boolean);
    }

    // variantUpsellMap is built by Liquid at render time and injected directly
    // into window.RTUpsellData - no JSON parsing needed, no data-attribute reading.
    // Format: { "variantId": ["handle:exhaust", "handle:filter", ...], ... }
    // Already available as d.variantUpsellMap from the Liquid <script> block.

    // ── productVariants fallback - fetch from product JSON if Liquid cache is stale ──
    if (!d.productVariants?.length && d.productHandle) {
      try {
        const r = await fetch(`/products/${d.productHandle}.js`);
        const p = await r.json();
        d.productVariants = (p.variants || []).map(v => ({ id: v.id, title: v.title }));
        if (DEBUG) console.warn('[RT] productVariants fetched:', d.productVariants.length);
      } catch (_) {}
    }

    // ── Run upsell logic (variant-aware) ────────────────────────────────────
    async function runUpsell(overrideVid) {
      // Best collection to surface as the "Explore more in …" CTA (hybrid).
      const collection = collectionFor(d);

      // L-1: Liquid-built variantUpsellMap - direct object lookup, zero parsing.
      // Liquid already matched every variant title/ID to its handles at render time.
      // overrideVid: variant ID from radio click (Hook B) before Xtra AJAX completes.
      if (d.variantUpsellMap && Object.keys(d.variantUpsellMap).length) {
        const vid = String(overrideVid || getSelectedVariantId());
        if (DEBUG) console.warn('[RT] variantUpsellMap lookup, vid:', vid, overrideVid ? '(radio)' : '(DOM)');
        const handles = d.variantUpsellMap[vid];
        if (handles && handles.length) {
          if (DEBUG) console.warn('[RT] ✓ Hit:', handles);
          const picks = await fetchHandlePicks(handles);
          if (picks.length) { render(picks, d, collection); return; }
        } else {
          if (DEBUG) console.warn('[RT] No map entry for', vid, '- keys:', Object.keys(d.variantUpsellMap));
        }
      }

      // L0: static handle overrides
      if (d.manualUpsellEntries?.length) {
        const picks = await fetchHandlePicks(d.manualUpsellEntries);
        if (picks.length) { render(picks, d, collection); return; }
      }

      // Algorithm fallback - render bundle if found, else the collection-only
      // card so the slot still helps the rider discover the right category.
      const upsells = await findUpsells(d, 2);
      if (upsells.length || collection) { render(upsells, d, collection); return; }

      // RT PERF (CLS): nothing to show at all - collapse the reserved space.
      const rootEl = document.getElementById('rt-smart-upsell-root');
      if (rootEl && !rootEl.querySelector('.rt-up')) rootEl.classList.add('rt-up-empty');
    }

    try {
      await runUpsell();
    } catch (e) { console.warn('[RT Upsell] init error:', e); }

    // ── Listen for variant changes - Xtra-native detection ────────────────────
    // Xtra does NOT update the URL on variant change (only xclusive does).
    // Two primary hooks + one safety-net cover every variant interaction:
    //
    // Hook A - MutationObserver on .f8pr-variant-selection parent
    //   Xtra’s AJAX re-render replaces the .f8pr-variant-selection element.
    //   The new element carries data-current-variant="<new_id>".
    //   We watch for that attribute appearing in the DOM.
    //
    // Hook B - delegated change event on radio/select inputs
    //   Fires the instant the rider clicks a variant button.
    //   Debounced 80ms so the AJAX swap can complete and P1 is readable.
    //
    // Hook C - lightweight 800ms interval safety-net
    //   Catches keyboard navigation / programmatic variant changes.
    //
    // All hooks share one debounced handler - no stacked fetches.

    let _lastVid = getSelectedVariantId();
    let _runTimer = null;

    async function _onVariantChange(forcedVid) {
      const cur = forcedVid || getSelectedVariantId();
      if (cur === _lastVid) return;
      _lastVid = cur;
      if (DEBUG) console.warn('[RT] Variant changed →', cur, forcedVid ? '(from radio)' : '(from DOM)');

      // Smooth crossfade: fade out current content while fetching new products.
      // New content fades in after fetch - no jarring wipe or loading flash.
      const root2 = document.getElementById('rt-smart-upsell-root');
      const inner = root2 && root2.querySelector('.rt-up');
      if (inner) {
        inner.style.transition = 'opacity 0.18s ease';
        inner.style.opacity = '0.35';
        inner.style.pointerEvents = 'none';
      }
      try { await runUpsell(cur); } catch (_) {}
    }

    function _scheduleVariantChange(forcedVid) {
      clearTimeout(_runTimer);
      _runTimer = setTimeout(function() { _onVariantChange(forcedVid); }, 80);
    }

    // Hook A: MutationObserver - fires after Xtra’s AJAX DOM swap
    const _observeTarget =
      document.querySelector('#main-product .m6pr, .m6pr, form.f8pr')?.parentElement ||
      document.querySelector('#main-product, .m6pr') ||
      document.body;

    const _variantObserver = new MutationObserver(function(mutations) {
      const relevant = mutations.some(function(m) {
        // Attribute mutation directly on [data-current-variant]
        if (m.type === 'attributes' && m.attributeName === 'data-current-variant') return true;
        // Node added that contains or IS [data-current-variant]
        return Array.from(m.addedNodes).some(function(n) {
          return n.nodeType === 1 && (
            n.hasAttribute('data-current-variant') ||
            n.querySelector('[data-current-variant]')
          );
        });
      });
      if (relevant) _scheduleVariantChange();
    });

    _variantObserver.observe(_observeTarget, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-current-variant'],
    });

    // Hook B: delegated change event - survives Xtra DOM replacement.
    //
    // Xtra renders variants in TWO different ways depending on product.options.size:
    //
    // A) name="variant-id" (multi-option products or dropdown style)
    //    input.value = numeric variant ID → use directly as forcedVid
    //
    // B) name="options[sectionId-OptionName]" (single-option products: options.size == 1)
    //    input.value = TEXT label e.g. "Hunter 350 (2022-2024)" → NOT a variant ID
    //    Must resolve to numeric ID via productVariants title lookup.
    //
    // In both cases [data-current-variant] still holds the OLD id at change time
    // (Xtra’s AJAX swap takes 200–500ms), so we cannot read from DOM.
    function _resolveVariantId(inputName, inputValue) {
      // Case A: direct numeric ID
      if (inputName === 'variant-id' || inputName === 'id') {
        return String(inputValue || '');
      }
      // Case B: options[...] - text value, resolve via title match
      if (inputName && inputName.startsWith('options[')) {
        const variants = window.RTUpsellData?.productVariants || [];
        const title = String(inputValue || '').trim().toLowerCase();
        const match = variants.find(v => v.title.trim().toLowerCase() === title);
        if (match) {
          if (DEBUG) console.warn('[RT] Resolved "' + inputValue + '" → variant ID', match.id);
          return String(match.id);
        }
        // Partial match fallback (handles year ranges in title)
        const partial = variants.find(v => {
          const vt = v.title.trim().toLowerCase();
          return vt.includes(title) || title.includes(vt);
        });
        if (partial) {
          if (DEBUG) console.warn('[RT] Partial match "' + inputValue + '" → variant ID', partial.id);
          return String(partial.id);
        }
        if (DEBUG) console.warn('[RT] Could not resolve variant title:', inputValue, 'variants:', variants.map(v=>v.title));
      }
      return '';
    }

    document.addEventListener('change', function(e) {
      const t = e.target;
      if (
        t.matches('input[name="variant-id"]') ||
        t.matches('select[name="variant-id"]') ||
        t.matches('input[name="id"].product-variant-id') ||
        (t.matches('input[type="radio"], select') && t.name && t.name.startsWith('options['))
      ) {
        const forcedVid = _resolveVariantId(t.name, t.value);
        if (DEBUG) console.warn('[RT] Hook B change, name:', t.name, 'value:', t.value, '→ forcedVid:', forcedVid);
        if (forcedVid) _scheduleVariantChange(forcedVid);
      }
    });

    // Hook C: 800ms safety-net interval (catches keyboard nav / programmatic changes)
    setInterval(function() {
      if (document.hidden) return; // RT FIX (INP): skip main-thread work on backgrounded tabs
      const cur = getSelectedVariantId();
      if (cur !== _lastVid) _scheduleVariantChange();
    }, 800);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    setTimeout(init, 200);
  }
})();